/*
 *
 * CircuitReport
 *
 */

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import moment from 'moment';

import FormControl from '@mui/material/FormControl';
import SelectField, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';

import SendIcon from '@mui/icons-material/Send';
import LinkIcon from '@mui/icons-material/Link';
import RenewIcon from '@mui/icons-material/Autorenew';

import Spacings from '~/modules/ui-kit/spacings';
import { useAvailableCircuitReports } from '~/hooks/api/useAvailableCircuitReports';
import { useCircuitReports } from '~/hooks/api/useCircuitReports';
import { useProlongCircuitReportLink } from '~/hooks/api/useProlongCircuitReportLink';
import { CircuitReportResource } from '~/generated';
import Typography from '@mui/material/Typography';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import messages from './messages';

const route = getRouteApi('/reports/$reportType');

export const CircuitReport = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const search = route.useSearch();
  const year = search.year ?? 0;
  const quarter = search.quarter ?? 0;
  const {
    data: availableReports,
    isFetching: isFetchingAvailableReports,
    refetch,
  } = useAvailableCircuitReports();
  const handleCopyReportData = (report: CircuitReportResource) => {
    navigator.clipboard.writeText(report.downloadLink!).then(() => {
      showSnackbar({
        message: intl.formatMessage(messages.copySuccessful),
      });
    });
  };
  const { data: reports, isFetching } = useCircuitReports(year, quarter);
  const prolongCircuitReportLinkMutation = useProlongCircuitReportLink({
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.prolongDownloadLinkSuccess),
      });
      refetch();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.prolongDownloadLinkFailed),
      });
    },
  });

  const handleSelectQuarter = (event: SelectChangeEvent) => {
    navigate({
      to: '/reports/$reportType',
      params: { reportType: 'circuit' },
      search: { year, quarter: Number(event.target.value), month: undefined },
    });
  };

  const handleSelectYear = (event: SelectChangeEvent) => {
    navigate({
      to: '/reports/$reportType',
      params: { reportType: 'circuit' },
      search: { year: Number(event.target.value), quarter, month: undefined },
    });
  };
  return (
    <Spacings.Inset>
      <Spacings.Stack scale="xl">
        <Spacings.Inline scale="m">
          {!isFetchingAvailableReports && availableReports && (
            <>
              <div>
                <FormControl variant="standard" style={{ width: 180 }}>
                  <InputLabel id="circuit-report-year-select-label">
                    <FormattedMessage {...messages.year} />
                  </InputLabel>
                  <SelectField
                    variant="standard"
                    labelId="circuit-report-year-select-label"
                    value={String(year)}
                    onChange={handleSelectYear}
                  >
                    {availableReports
                      .reduce(
                        (acc, report) =>
                          !acc.includes(report.year!)
                            ? [...acc, report.year!]
                            : acc,
                        [] as number[]
                      )
                      .map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                  </SelectField>
                </FormControl>
              </div>
              <div>
                <FormControl variant="standard" style={{ width: 180 }}>
                  <InputLabel id="circuit-report-querter-select-label">
                    <FormattedMessage {...messages.quarter} />
                  </InputLabel>
                  <SelectField
                    variant="standard"
                    labelId="circuit-report-querter-select-label"
                    value={String(quarter)}
                    onChange={handleSelectQuarter}
                  >
                    {availableReports
                      .filter((report) => report.year === year)
                      .map((report) => (
                        <MenuItem
                          key={report.quarterOfYear}
                          value={report.quarterOfYear}
                        >
                          {report.quarterOfYear}
                        </MenuItem>
                      ))}
                  </SelectField>
                </FormControl>
              </div>
            </>
          )}
        </Spacings.Inline>
        {!isFetching && reports && (
          <Spacings.Stack scale="l">
            {reports.map((reportForCircuit, index) => (
              <React.Fragment key={reportForCircuit.circuitId}>
                {index > 0 && (
                  <div>
                    <Divider />
                  </div>
                )}
                <Spacings.Stack>
                  <Spacings.Stack scale="none">
                    <Typography variant="h6">
                      {reportForCircuit.circuitName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {reportForCircuit.circuitOverseerName} •{' '}
                      {reportForCircuit.circuitOverseerEmail}
                    </Typography>
                  </Spacings.Stack>
                  <Spacings.Inline scale="s">
                    {moment().isBefore(
                      moment(reportForCircuit.downloadLinkValidUntil)
                    ) ? (
                      <>
                        <div>
                          <Button
                            startIcon={<LinkIcon />}
                            onClick={() =>
                              handleCopyReportData(reportForCircuit)
                            }
                          >
                            <FormattedMessage {...messages.copyReport} />
                          </Button>
                        </div>
                        <div>
                          <Button
                            startIcon={<SendIcon />}
                            href={`mailto:${encodeURIComponent(
                              reportForCircuit.circuitOverseerEmail!
                            )}?subject=${encodeURIComponent(
                              `ÖZB Bericht für Verkündiger aus Kreis ${reportForCircuit.circuitName}`
                            )}&body=${encodeURIComponent(
                              reportForCircuit.downloadLink!
                            )}`}
                          >
                            <FormattedMessage {...messages.prefillEmail} />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <Button
                        disabled={prolongCircuitReportLinkMutation.isPending}
                        startIcon={<RenewIcon />}
                        onClick={() =>
                          prolongCircuitReportLinkMutation.mutate({
                            circuitId: reportForCircuit.circuitId!,
                            year: reportForCircuit.year!,
                            quarter: reportForCircuit.quarter!,
                          })
                        }
                      >
                        <FormattedMessage {...messages.prolongDownloadLink} />
                      </Button>
                    )}
                  </Spacings.Inline>
                </Spacings.Stack>
              </React.Fragment>
            ))}
          </Spacings.Stack>
        )}
      </Spacings.Stack>
    </Spacings.Inset>
  );
};

export default CircuitReport;
